import { Checkbox } from '@liveauctioneers/hammer-ui-core/checkbox';
import { IconButton } from '@liveauctioneers/hammer-ui-core/iconButton';
import { Trash } from '@liveauctioneers/hammer-ui-core/icons';
import { BodySecondary, Caption } from '@liveauctioneers/hammer-ui-core/text';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { useCallback } from 'react';

import type { SimilarItemAlert } from '@/redux/modules/similarItemAlerts/similarItemAlerts.types';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getItemSummaryFirstImageUrl, getItemSummaryTitle } from '@/redux/modules/item/summary/itemSummary.selectors';
import { openSimilarItemAlertDeleteModal } from '@/redux/modules/modal';
import { toggleSimilarItemAlertEmail } from '@/redux/modules/similarItemAlerts/similarItemAlerts.actions';
import { shortFormatDate } from '@/utils/dates';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import { SimilarItemAlertsTableRow } from './SimilarItemAlertsTableRow';

type Props = {
    alert: SimilarItemAlert;
};

// savedsearch-api returns the Go zero-value time (`0001-01-01T00:00:00Z`) for a lot that has never
// triggered an email — match it by year, not an exact string, since the wire format carries a `Z`
// and parses to "Dec 31, 0001" after the tz offset. Anything at year 1 or earlier is "never sent".
const hasNeverSent = (lastSent: string) => {
    if (!lastSent) {
        return true;
    }
    const parsed = parseISO(lastSent);
    return Number.isNaN(parsed.getTime()) || parsed.getUTCFullYear() <= 1;
};

/**
 * One row of the Emails & Notifications "Similar Item Alerts" section. The lot's title and thumbnail
 * come from the shared item-summary slice (hydrated by the section); a lot that could not be
 * hydrated still renders using the title savedsearch-api stored on the row, so a gap in item-api
 * never blanks the row.
 */
export const SimilarItemAlertRow = ({ alert }: Props) => {
    const dispatch = useAppDispatch();
    const formatMessage = useAppFormatMessage();

    const hydratedTitle = useAppSelector((state) => getItemSummaryTitle(state, alert.lotId));
    const imageUrl = useAppSelector((state) => getItemSummaryFirstImageUrl(state, alert.lotId));

    const title = hydratedTitle || alert.lotTitle;
    const lastAlert = hasNeverSent(alert.lastSent)
        ? String(formatMessage(['never']))
        : shortFormatDate(parseISO(alert.lastSent));
    const dateAdded = shortFormatDate(parseISO(alert.savedAt));

    const onDelete = useCallback(() => {
        dispatch(
            openSimilarItemAlertDeleteModal({
                daysSinceAdded: differenceInCalendarDays(new Date(), parseISO(alert.savedAt)),
                lotId: alert.lotId,
                lotTitle: title,
                savedItemId: alert.savedItemId,
            })
        );
    }, [alert.lotId, alert.savedAt, alert.savedItemId, dispatch, title]);

    const onToggleEmail = useCallback(() => {
        dispatch(toggleSimilarItemAlertEmail({ savedItemId: alert.savedItemId, sendAlert: !alert.sendAlert }));
    }, [alert.savedItemId, alert.sendAlert, dispatch]);

    return (
        <SimilarItemAlertsTableRow
            dateAdded={<BodySecondary>{dateAdded}</BodySecondary>}
            email={
                <Checkbox.Option
                    aria-label={String(
                        formatMessage(['notificationsPage', 'toggleEmailNotifications'], { values: { name: title } })
                    )}
                    checked={alert.sendAlert}
                    className="mb-0"
                    name="similarItemAlertEmailCheckbox"
                    onChange={onToggleEmail}
                />
            }
            item={
                <div className="flex min-w-0 items-center gap-sm">
                    <IconButton
                        aria-label={String(formatMessage(['similarItemAlerts', 'deleteAlertLabel']))}
                        icon={Trash}
                        onClick={onDelete}
                        size="sm"
                    />
                    {/* getItemSummaryFirstImageUrl falls back to the shared placeholder image, so
                        there is always something to render — no empty-square branch needed. */}
                    <img
                        alt={title}
                        className="size-[48px] shrink-0 rounded-xs object-cover"
                        src={imageUrl}
                    />
                    <div className="min-w-0">
                        <BodySecondary className="line-clamp-2">{title}</BodySecondary>
                        <Caption
                            className="tabletMin:hidden"
                            color="secondary"
                        >
                            {formatMessage(['similarItemAlerts', 'lastAlertMobile'], { values: { date: lastAlert } })}
                        </Caption>
                    </div>
                </div>
            }
            lastAlert={<BodySecondary>{lastAlert}</BodySecondary>}
        />
    );
};
